import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  SearchEmployeeHRRefService,
  SearchEmployeeService,
} from '@ups/xplat/api/services';
import { IDynamicModel } from '@ups/xplat/dynamic-render';

import { DynamicTypeaheadEmployeeWebBaseComponent } from '../base/dynamic-typeahead-employee.web-base-component';
import { HttpParams } from '@angular/common/http';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadEmployeeComponent),
  multi: true,
};

@Component({
  selector: 'ups-dynamic-typeahead-employee',
  templateUrl: 'dynamic-typeahead-employee.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  // WI 35584: template state is marked at each async write (loading, search results, target
  // updates) in the shared dynamic-render bases.
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-commit-allow: declared in the dynamic-render NgModule with its typeahead siblings (Task 35874)
  standalone: false,
})
export class DynamicTypeaheadEmployeeComponent
  extends DynamicTypeaheadEmployeeWebBaseComponent
  implements OnInit
{
  showInActive = false; // means "show inactive employees"

  private readonly searchService = inject(SearchEmployeeService);
  private readonly hrRefSearchService = inject(SearchEmployeeHRRefService);

  ngOnInit() {
    super.ngOnInit();
    // Task 35874: the single-select is [value]-bound; repaint on every control write.
    if (!this.config?.options?.isMultiSelect) {
      this.bindViewToControl();
    }

    this.searchQuery = this.performSearch.bind(this);
  }

  // Called by base class filterChangeItem(query, params)
  performSearch(searchString: string, params?: HttpParams, limit = 20) {
    // Convert “Show Inactive” → isActive flag
    const showInActive = this.showInActive;

    if (this.config?.options?.employeePicker) {
      return this.hrRefSearchService.searchPicker(
        searchString,
        params,
        limit,
        this.config?.options?.favoriteType ?? null,
        this.favoriteVpCompanyId()
      );
    }

    if (this.config?.options?.distinctHRRef) {
      return this.hrRefSearchService.search(
        searchString,
        params,
        limit,
        this.config?.options?.favoriteType ?? null,
        this.favoriteVpCompanyId()
      );
    }

    return this.searchService.search(
      searchString,
      params,
      limit,
      showInActive,
      this.config?.options?.allowToggleActive,
      this.config?.options?.favoriteType ?? null,
      this.favoriteVpCompanyId()
    );
  }

  /** Use for template: handles both PascalCase and camelCase from API */
  isFavorite(item: {
    IsUserFavorite?: boolean;
    isUserFavorite?: boolean;
  }): boolean {
    return !!(item?.IsUserFavorite ?? item?.isUserFavorite);
  }

  toggleFavorite(
    employee: {
      HRRef?: number | null;
      IsUserFavorite?: boolean;
      isUserFavorite?: boolean;
    },
    e: PointerEvent
  ) {
    if (employee?.HRRef == null || !this.config?.options?.favoriteType) return;
    const current = this.isFavorite(employee);
    (employee as { IsUserFavorite?: boolean }).IsUserFavorite = !current;
    (employee as { isUserFavorite?: boolean }).isUserFavorite = !current;
    this.searchService
      .addOrRemoveFavoriteEmployee(
        employee.HRRef,
        this.config.options.favoriteType
      )
      .then(() => {});
    e.stopPropagation();
  }

  public showCellMask(config: IDynamicModel): boolean {
    let showMask = false;
    if (config) {
      if (config?.formControlName) {
        if (
          config?.formControlName === 'businessDevelopmentId' ||
          config?.formControlName === 'estimatorId'
        ) {
          showMask = true;
        }
      }
    }

    return showMask;
  }

  onToggleActive() {
    this.showInActive = !this.showInActive;
    this.searchQuery = this.performSearch.bind(this);
  }
}
